import React, { useEffect } from "react";
import { Clock, SkipForward } from "lucide-react";
import { haptic } from "../lib/haptics";
import { playRestEndChime } from "../lib/chime";

/* -------------------- component -------------------- */

/**
 * Between-sets rest countdown. Mount only when active; pass `remaining` and
 * `restSec` from the parent so the visual progress bar matches the prescribed
 * window. The parent owns the ticking state so it can clear on unmount /
 * skip-rest gracefully — this component just renders.
 *
 * On mount when `remaining <= 0` we play the success chime + haptic and call
 * `onComplete()` so the parent can null its state. The chime is routed through
 * `lib/chime.js` which auto-resumes a suspended AudioContext so the beep stays
 * audible even while Spotify Web Playback SDK is streaming.
 */
export default function RestTimer({ remaining, restSec, onSkip, onComplete, testid }) {
  useEffect(() => {
    if (remaining !== null && remaining <= 0) {
      playRestEndChime();
      haptic.success();
      onComplete?.();
    }
  }, [remaining, onComplete]);

  if (remaining === null || remaining <= 0) return null;

  const mm = Math.floor(remaining / 60);
  const ss = String(remaining % 60).padStart(2, "0");
  const pct = 100 - (remaining / restSec) * 100;

  return (
    <div
      className="mt-3 flex items-center gap-3 px-3 py-2 border border-[#6B9BFF]/40 bg-[#6B9BFF]/5"
      data-testid={testid}
    >
      <Clock className="w-4 h-4 text-[#6B9BFF]" />
      <div className="flex-1">
        <div className="label-eyebrow text-[#6B9BFF]">Rest</div>
        <div className="font-display text-xl tabular-nums">
          {mm}:{ss}
        </div>
      </div>
      <div className="flex-1 h-1 bg-white/10">
        <div className="h-full bg-[#6B9BFF] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <button
        type="button"
        onClick={onSkip}
        className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1"
        data-testid={`${testid}-skip`}
      >
        <SkipForward className="w-3.5 h-3.5" /> Skip
      </button>
    </div>
  );
}
